// WP-FP1a: the sole projection boundary for line judges and retrieval queries.
// Raw rows stay raw. The ON sibling projection is request-only.
import { isBlankCorrection } from './classify-det.js';

function rejoinOriginalField(value) {
  const raw = value == null ? '' : String(value);
  let previous = 0, cursor = 0, output = '';
  for (let at = raw.indexOf(' | '); at !== -1; at = raw.indexOf(' | ', previous)) {
    output += raw.slice(cursor, at);
    if (at - previous !== 60) output += ' | ';
    previous = at + 3;
    cursor = previous;
  }
  return output + raw.slice(cursor);
}
export function fpStory(row) {
  const lines = [];
  const operation = String(row.opDescription ?? '').trim();
  if (operation) lines.push(`Operation: ${operation}`);
  for (const field of ['complaint', 'cause', 'correction']) {
    const text = rejoinOriginalField(row[field]);
    lines.push(`${field[0].toUpperCase() + field.slice(1)}: ${text.trim() ? text : '(blank)'}`);
  }
  return lines.join('\n');
}
export const fpHolisticText = r => [r.correction, r.cause, r.complaint].map(x => (x || '').trim()).filter(Boolean).join(' — ');
export const fpAssociationText = r => `${r.correction || ''} ↔ ${r.anchorCorr || ''}`;
export const fpCanonicalTexts = rows => rows.map(r => r._canonical || '');
export const fpPrecedentCard = list => (list || []).slice(0, 3).map(p => ({ decision: p.decision, correction: (p.correction || '').slice(0, 120), why: (p.rationale || '').slice(0, 100), sim: Math.round((p.similarity || 0) * 100), uses: p.support_count }));
const finite = value => typeof value === 'number' && Number.isFinite(value) ? value : null;
const fullParts = row => (row.parts || []).map(p => ({ partNumber: p.partNumber ?? '', partDesc: p.partDesc ?? '' }));
function optionalFields(row) {
  const result = {};
  const code = String(row.opCode ?? '').trim();
  if (code) result.opCode = code;
  const vehicle = {};
  for (const key of ['make', 'model', 'year', 'mileage']) {
    if (row[key] != null && String(row[key]).trim()) vehicle[key] = row[key];
  }
  if (Object.keys(vehicle).length) result.vehicle = vehicle;
  return result;
}
const roKey = row => row.ro || `__blank_${row.idx}`;
export function fpSiblingList(row, group, arm = 'on') {
  const siblings = (group || []).filter(other => other !== row && !(row.idx !== undefined && other.idx === row.idx));
  if (arm === 'off') return siblings.map(r => ({ line: r.line, correction: r.correction, cause: (r.cause || '').slice(0, 160), complaint: (r.complaint || '').slice(0, 160) })).filter(r => r.line || r.correction || r.cause || r.complaint);
  return siblings.map(r => {
    const { opCode } = optionalFields(r);
    return { line: r.line, ...(opCode === undefined ? {} : { opCode }), story: fpStory(r), partsSale: finite(r.partsSale), laborSale: finite(r.laborSale), techHours: finite(r.techHours), parts: fullParts(r) };
  });
}
// Property insertion order is intentional in OFF: P2 compares JSON bytes to F.
function releasedInput(stage, r, id, precedents) {
  const parts = () => (r.parts || []).map(p => ({ partDesc: p.partDesc, partNumber: p.partNumber }));
  const roParts = () => (r.roParts || []).slice(0, 10).map(p => ({ line: p.line, partDesc: p.partDesc }));
  if (stage === 'anatomy') return { id, correction: r.correction || '', cause: r.cause || '', complaint: r.complaint || '' };
  if (stage === 'classify' || stage === 'understand') return { id, ro: r.ro, line: r.line, correction: r.correction, cause: r.cause, complaint: r.complaint, corrBlank: !r.correction?.trim(), cautionOutside: r.cautionOutside || false, roContext: r.roContext || [], parts: parts(), roParts: roParts(), precedents: fpPrecedentCard(precedents) };
  if (stage === 'validate') return { id, ruleDecision: r.result, ruleReasoning: r.reasoning, correction: r.correction, cause: r.cause, complaint: r.complaint, partsSale: r.partsSale, laborSale: r.laborSale, cautionOutside: r.cautionOutside || false, parts: parts(), roParts: roParts(), precedents: fpPrecedentCard(precedents) };
  if (stage === 'pre-analyze-HR') return { id, correction: r.correction || '', cause: r.cause || '', complaint: r.complaint || '', partsSale: r.partsSale, laborSale: r.laborSale, parts: (r.parts || []).map(p => ({ partDesc: p.partDesc })), roContext: (r.roContext || []).slice(0, 5), flow: r.flow, currentReasoning: r.reasoning };
  if (stage === 'fable-review') return { id, decision: r.result, reasoning: r.reasoning || '', confidence: r.aiConfidence ?? null, flow: r.flow, source: r.source, correction: r.correction || '', cause: r.cause || '', complaint: r.complaint || '', partsSale: r.partsSale, laborSale: r.laborSale, techHours: r.techHours, parts: parts(), anatomy: r.structured || r._structuredPre || null, roContext: (r.roContext || []).slice(0, 5) };
  throw new Error(`Unknown line stage: ${stage}`);
}
export function fpStageInput(stage, row, id, { arm = 'on', groups = {}, precedents = [] } = {}) {
  if (arm === 'off') return releasedInput(stage, row, id, precedents);
  const story = fpStory(row);
  if (stage === 'anatomy') return { id, story };
  const optional = optionalFields(row);
  const evidence = { ...optional, story, partsSale: finite(row.partsSale), laborSale: finite(row.laborSale), techHours: finite(row.techHours), parts: fullParts(row) };
  const roContext = fpSiblingList(row, groups[roKey(row)], 'on');
  if (stage === 'classify' || stage === 'understand') return { id, ro: row.ro, line: row.line, ...evidence, corrBlank: isBlankCorrection(row.correction), cautionOutside: row.cautionOutside || false, roParts: (row.roParts || []).map(p => ({ line: p.line, partDesc: p.partDesc ?? '' })), roContext, precedents: fpPrecedentCard(precedents) };
  if (stage === 'validate') return { id, ruleDecision: row.result, ruleReasoning: row.reasoning, ...evidence, cautionOutside: row.cautionOutside || false, roParts: (row.roParts || []).map(p => ({ line: p.line, partDesc: p.partDesc ?? '' })), roContext, precedents: fpPrecedentCard(precedents) };
  if (stage === 'pre-analyze-HR') return { id, ...evidence, roContext, flow: row.flow, currentReasoning: row.reasoning };
  if (stage === 'fable-review') return { id, decision: row.result, reasoning: row.reasoning || '', confidence: row.aiConfidence ?? null, flow: row.flow, source: row.source, ...evidence, anatomy: row.structured || row._structuredPre || null, roContext };
  throw new Error(`Unknown line stage: ${stage}`);
}
export function fpJoinedParts(row, partsMap, state, arm) {
  const key = `${row.ro}:${row.line}`;
  return partsMap[key] || (state === 'CA' && arm === 'on' ? row.parts || [] : []);
}
export function fpRunPlan(rows, partsMap = {}, state = 'CA', ai = true, selectedArm = 'on') {
  const arm = state === 'CA' && selectedArm === 'on' ? 'on' : 'off';
  const work = rows.map(r => ({ ...r, parts: fpJoinedParts(r, partsMap, state, arm), _fullPicture: ai ? arm : 'off' }));
  const groups = {};
  for (const row of work) (groups[roKey(row)] ||= []).push(row);
  const roPartsCache = {};
  for (const row of work) {
    if (!roPartsCache[row.ro]) {
      const all = [];
      for (const [key, parts] of Object.entries(partsMap)) if (key.startsWith(row.ro + ':')) all.push(...parts.map(p => ({ ...p, line: key.split(':')[1] })));
      roPartsCache[row.ro] = all;
    }
    const mapped = Object.prototype.hasOwnProperty.call(partsMap, `${row.ro}:${row.line}`);
    row.roParts = (arm === 'on' && !mapped && row.roParts ? row.roParts : roPartsCache[row.ro]).filter(p => p.line !== row.line);
    row.roContext = fpSiblingList(row, groups[roKey(row)], 'off');
  }
  return { work, groups, arm };
}
export const FP_OP_POSITION_ERROR = 'This file has an Op Description column among the standard columns. Move it after the last standard column and upload again.';
export function fpOpColumn(headers, hasOpCode, hasDiscount) {
  const indexes = headers.flatMap((h, i) => ['op description', 'operation description'].includes(String(h).trim().toLowerCase()) ? [i] : []);
  const last = (hasOpCode ? 10 : 9) + (hasDiscount ? 3 : 0);
  return { index: indexes[0] ?? -1, error: indexes.some(i => i <= last) ? FP_OP_POSITION_ERROR : null };
}
export const fpOpValue = (cells, index) => index < 0 ? '' : String(cells[index] ?? '').trim();
export function fpFailureCounts() {
  const counts = {};
  const stages = ['anatomy', 'classify', 'understand-all', 'validate', 'pre-analyze-HR', 'fable-review'];
  return {
    add(stage) { if (!stages.includes(stage)) throw new Error(`Unknown failed AI stage: ${stage}`); counts[stage] = (counts[stage] || 0) + 1; },
    snapshot: () => ({ ...counts }),
    label: () => { const nonzero = stages.filter(s => counts[s]).map(s => `${s} ${counts[s]}`); return nonzero.length ? 'AI batches failed: ' + nonzero.join(' · ') : ''; },
  };
}
